/**
 * Accounting domain event schemas.
 *
 * Typed payload validation for all accounting-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module accounting.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const InvoiceAutoGenerateEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('invoice_auto_generate'),
  payload: z.object({
    sourceProposalId: z.string().optional(),
    clientId: z.string(),
    opportunityId: z.string().optional(),
    amount: z.number(),
    trigger: z.string().optional(),
    source: z.string(),
  }),
})

export const CommissionCalculatedEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('commission_calculated'),
  payload: z.object({
    invoiceId: z.string(),
    invoiceNumber: z.string().optional(),
    clientId: z.string(),
    clientName: z.string().optional(),
    totalAmount: z.number(),
    totalCommission: z.number(),
    commissionType: z.string().optional(),
    splits: z.array(z.record(z.unknown())).optional(),
    calculatedAt: z.string().optional(),
    sourceEventId: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const XeroSyncStatusQueriedEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('xero_sync_status_queried'),
  payload: z.object({
    query: z.string().optional(),
    syncType: z.string().optional(),
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    stats: z.record(z.unknown()).optional(),
    source: z.string().optional(),
  }),
})

export const XeroSyncTriggeredEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('xero_sync_triggered'),
  payload: z.object({
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    syncDirection: z.string().optional(),
    mappingId: z.string().optional(),
    logId: z.string().optional(),
    success: z.boolean().optional(),
    source: z.string().optional(),
  }),
})

export const XeroSyncCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('xero_sync_completed'),
  payload: z.object({
    logId: z.string().optional(),
    mappingId: z.string().optional(),
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    crmId: z.string().optional(),
    operation: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const XeroSyncFailedEvent = BackboneEventSchema.extend({
  domain: z.literal('accounting'),
  type: z.literal('xero_sync_failed'),
  payload: z.object({
    logId: z.string().optional(),
    mappingId: z.string().optional(),
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    error: z.string().optional(),
    source: z.string().optional(),
  }),
})

// ── All accounting events ───────────────────────────────────────────────

export const AccountingDomainEvents = [
  InvoiceAutoGenerateEvent,
  CommissionCalculatedEvent,
  XeroSyncStatusQueriedEvent,
  XeroSyncTriggeredEvent,
  XeroSyncCompletedEvent,
  XeroSyncFailedEvent,
] as const
